/**
 * Save As Template Modal
 * 
 * Saves the current funnel's draft as a template.
 * Template is saved under the tenant's "My Templates".
 */

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, FileText } from "lucide-react";
import { useSaveAsTemplate } from "@/hooks/useFunnelTemplates";

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "lead_capture", label: "Lead Capture" },
  { value: "qualification", label: "Qualification" },
  { value: "appointment", label: "Appointment" },
  { value: "insurance", label: "Insurance" },
  { value: "financial", label: "Financial" },
];

export default function SaveAsTemplateModal({
  open,
  onClose,
  funnelId,
  funnelName,
}) {
  const [name, setName] = useState(funnelName ? `${funnelName} Template` : "");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  
  const saveAsTemplate = useSaveAsTemplate();
  
  const handleSave = async () => {
    if (!name.trim()) return;
    
    try {
      await saveAsTemplate.mutateAsync({
        sourceFunnelId: funnelId,
        name: name.trim(),
        description: description.trim() || null,
        category,
      });
      onClose();
    } catch (error) {
      // Error handled by mutation
    }
  };
  
  const handleClose = () => {
    setName(funnelName ? `${funnelName} Template` : "");
    setDescription("");
    setCategory("general");
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Save as Template
          </DialogTitle>
          <DialogDescription>
            Save this funnel's current draft as a reusable template.
            The template will be available in "My Templates".
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">Template Name</Label>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., My Lead Qualification Template"
              autoFocus
              data-testid="template-name-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="template-description">Description (optional)</Label>
            <Textarea
              id="template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of what this template is for..."
              className="min-h-[80px]"
              data-testid="template-description-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="template-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="template-category" data-testid="template-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || saveAsTemplate.isPending}
            data-testid="save-template-btn"
          >
            {saveAsTemplate.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Template"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
